import {
	DocumentData,
	DocumentSnapshot,
	QueryConstraint,
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDocs,
	limit,
	orderBy,
	query,
	serverTimestamp,
	startAfter,
	updateDoc,
} from 'firebase/firestore';

import {db} from '../firebase';
import {currentlyMasterFirebaseUser, getDebug} from './GeneralUse';
import {useNewsStore} from '../stores/NewsStore';

//
// ****************************************************************************************************
// news/{documentId}.uid === firebaseAuthUser.uid === (currently only) master Firebase user aka
// whirlwind owner/creator's uid.
//
// Interact with the Firestore news collection. CREATE/UPDATE/DELETE restricted to the master Firebase
// user aka whirlwind owner/creator only. Anyone can READ.
//
// METHODS
// createNewsDocument
// deleteNewsDocument
// readNewsDocuments (NOT EXPORTED)
// readNewsDocumentsLimit
// refreshNewsDocuments
// updateNewsDocumentField
//
const DEBUG = getDebug('news');
// ****************************************************************************************************
//

/*
//
*/
export const createNewsDocument = async (
	content: string,
	title: string,
	uid: string,
	username: string,
) => {
	DEBUG &&
		console.trace(`createNewsDocument('${content}', '${title}', '${uid}')`);

	try {
		if (currentlyMasterFirebaseUser()) {
			const newsCollection = collection(db, 'news');

			const newsDoc = {
				content: content,
				created_at: serverTimestamp(),
				last_edited_at: serverTimestamp(),
				title: title,
				uid: uid,
				username: username,
			};

			await addDoc(newsCollection, newsDoc);
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const deleteNewsDocument = async (documentId: string) => {
	DEBUG && console.trace(`deleteNewsDocument(${documentId})`);

	try {
		const docRef = doc(db, 'news', documentId);

		await deleteDoc(docRef);
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
// We HAVE to specify a valid field.
// We HAVE to specify a valid sort direction.
// We HAVE to specify whether or not we use the query cursor (i.e. do we want to return only the docs STARTING AFTER the lastVisibleNews document location in the query result?)
// We OPTIONALLY specify a limit to the number of docs returned.
//
// How to use (1 = returned document from with the total [collection], 0 = not returned)
// First call: readNewsDocuments('created_at', 'desc', true, 5) ===> returns [1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
// Second call: readNewsDocuments('created_at', 'desc', true, 5) ===> returns [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0]
// Third call: readNewsDocuments('created_at', 'desc', true, 5) ===> returns [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1]
//
// This essentially allows us to do something like: scroll through a list of news posts ordered by creation date, loading another four at a time when we scroll to the
// bottom of the existing container.
*/
const readNewsDocuments = async (
	fieldName: 'created_at' | 'last_edited_at' | 'title',
	sortDirection: 'asc' | 'desc',
	useCursor: boolean,
	limitCount?: number,
) => {
	DEBUG &&
		console.trace(
			`readNewsDocuments('${fieldName}', '${sortDirection}', ${useCursor}, ${limitCount})`,
		);

	try {
		// Zustand
		const lastVisibleNews = useNewsStore.getState().lastVisibleNews;

		const newsCollection = collection(db, 'news');

		const queryConstraints: QueryConstraint[] = [
			orderBy(fieldName, sortDirection),
		];
		if (useCursor && lastVisibleNews) {
			queryConstraints.push(startAfter(lastVisibleNews));
		} else {
			DEBUG && console.trace(`useCursor disabled or no lastVisibleNews`);
		}
		if (limitCount) {
			queryConstraints.push(limit(limitCount));
		} else {
			DEBUG && console.trace(`no limitCount provided`);
		}

		const q = query(newsCollection, ...queryConstraints);

		const docSnaps = await getDocs(q);
		const docs = docSnaps.docs;

		return docs;
	} catch (error) {
		DEBUG && console.error(error);
		return null;
	}
};

/*
// This is the only way the frontend is allowed to use the readNewsDocuments() method. We neuter the front end, only allowing a CUSTOM BUT NON-NEGOTIABLE limit to the
// number of documents returned. Otherwise we are always returning from the latest news post to the earliest, and using the query cursor in the process.
*/
export const readNewsDocumentsLimit = async (limitCount: number) => {
	DEBUG && console.groupCollapsed('Populate News from Firestore');
	DEBUG && console.trace(`readNewsDocumentsLimit(${limitCount})`);

	try {
		// Zustand
		const news = useNewsStore.getState().news;
		const setNews = useNewsStore.getState().setNews;
		const setLastVisibleNews = useNewsStore.getState().setLastVisibleNews;
		const loadedAllNews = useNewsStore.getState().loadedAllNews;
		const setLoadedAllNews = useNewsStore.getState().setLoadedAllNews;

		if (limitCount >= 1 && !loadedAllNews) {
			const docs = await readNewsDocuments(
				'created_at',
				'desc',
				true,
				limitCount,
			);

			if (docs && docs.length) {
				let newNews: DocumentSnapshot<DocumentData, DocumentData>[] = [];
				if (news) {
					newNews = [...news, ...docs];
				} else {
					newNews = docs;
				}

				DEBUG && console.trace(`setNews(`, newNews, `)`);
				setNews(newNews);

				const lastVisibleNewsLive = docs[docs.length - 1];
				DEBUG &&
					console.trace(
						`setLastVisibleNews(`,
						lastVisibleNewsLive,
						`)
aka`,
						lastVisibleNewsLive.data(),
					);
				setLastVisibleNews(lastVisibleNewsLive);

				return true;
			} else {
				DEBUG &&
					console.log(
						`docs is either null (error in readNewsDocuments()) or empty`,
					);
				DEBUG && console.trace(`setLastVisibleNews(null)`);
				DEBUG && console.trace(`setLoadedAllNews(true)`);
				setLastVisibleNews(null);
				setLoadedAllNews(true);
			}
		} else {
			DEBUG && console.log(`limitCount is < 1 or we have loaded all`);
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	DEBUG && console.groupEnd();

	return false;
};

/*
//
*/
export const refreshNewsDocuments = async (limitCount: number) => {
	DEBUG && console.trace(`refreshNewsDocuments(${limitCount})`);

	try {
		const setNews = useNewsStore.getState().setNews;
		const setLastVisibleNews = useNewsStore.getState().setLastVisibleNews;
		const setLoadedAllNews = useNewsStore.getState().setLoadedAllNews;

		setNews(null);
		setLastVisibleNews(null);
		setLoadedAllNews(false);

		await readNewsDocumentsLimit(limitCount);
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const updateNewsDocumentField = async (
	documentId: string,
	field: 'content' | 'title',
	newVal: string,
) => {
	DEBUG &&
		console.trace(
			`updateNewsDocumentField('${documentId}', '${field}', '${newVal}')`,
		);

	try {
		const docRef = doc(db, 'news', documentId);

		await updateDoc(docRef, {
			[field]: newVal,
			last_edited_at: serverTimestamp(),
		});
	} catch (error) {
		DEBUG && console.error(error);
	}
};
